// KPI-28 — kataloğa dosyadan gösterge içe aktarma: `/api/v1/kpi/catalog-import/*` sarmalayıcıları.
// analyze HAM gövde (xlsx/docx MIME) ile gider — `api()` JSON'a sabit olduğu için burada fetch +
// `csrfHeaders()`; commit düz JSON. DTO'lar backend `catalog-import-plan.ts` şemasının aynası.

import type { KpiAggregation, KpiValueType } from "@mahir/shared";
import { ApiError, api, csrfHeaders } from "./client";

export type CatalogImportAction = "create" | "map_existing" | "skip";
/** Kırılım (spec §7.3): `breakdown_group` üstü toplam göstergesi, `breakdown_member` üstü grup. */
export type CatalogImportKind = "category" | "metric" | "breakdown_group" | "breakdown_member";

export interface CatalogImportRowDto {
  rowId: string;
  rowLabel: string;
  sheetName: string;
  depth: number;
  kind: CatalogImportKind;
  action: CatalogImportAction;
  code: string;
  label: string;
  unit: string | null;
  /** D9 — "TL | USD | EUR" gibi çoklu birim: ilki varsayılan, kalanı alternatif. */
  unitVariants?: string[];
  /** K-U1 — dosyadaki "Açıklama" / "Standart eşlemesi" sütunları (yalnız doluysa). */
  description?: string;
  requirements?: { standard: string; code: string }[];
  categoryCode: string | null;
  valueType: KpiValueType;
  isDerived: boolean;
  formula: string | null;
  aggregation: KpiAggregation;
  confidence: number;
  note?: string;
  sample?: string;
  existing?: {
    id: string;
    code: string;
    label: string;
    unit: string | null;
    isCategory: boolean;
  } | null;
  update?: boolean;
  /** Kırılım grubu: boyut + tamlık + toplama kuralı (sunucu `CatalogImportRowSchema` aynası). */
  dimension?: string;
  complete?: boolean;
  rollup?: "sum" | "avg" | "none";
  /** Kırılım üyesi slug'ı. */
  member?: string;
}

export interface CatalogImportAnalysisDto {
  fileName: string;
  rows: CatalogImportRowDto[];
  warnings: string[];
  stats: {
    rows: number;
    categories: number;
    create: number;
    mapExisting: number;
    skip: number;
    llmRows: number;
    chunks: number;
    unanswered: number;
  };
  existingCategories: { code: string; label: string; parentCode: string | null }[];
}

export interface CatalogImportCommitResultDto {
  createdCategories: number;
  createdMetrics: number;
  updated: number;
  mapped: number;
  skipped: number;
  codes: string[];
  /** Yalnız `packageId` verildiyse: pakete eklenen kalem sayısı (kapanışla gelenler dahil). */
  packageItemsAdded?: number;
  /** Yalnız `packageId` verildiyse: pakete giremeyen yeni kodlar (kategori, kök metrik, yetim kırılım). */
  packageSkipped?: string[];
}

const MIME_BY_EXT: Record<string, string> = {
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pdf: "application/pdf",
};

export function catalogImportMime(fileName: string): string | null {
  const ext = fileName.toLowerCase().split(".").pop() ?? "";
  return MIME_BY_EXT[ext] ?? null;
}

/** Dosya → öneri satırları (senkron; sunucu tavanı 60 sn, PDF 3 dk). */
export async function analyzeCatalogImport(file: File): Promise<CatalogImportAnalysisDto> {
  const mime = catalogImportMime(file.name);
  if (!mime) throw new ApiError(400, "KPI_UNSUPPORTED_FORMAT", "Yalnız .xlsx, .docx ve .pdf");
  const res = await fetch(
    `/api/v1/kpi/catalog-import/analyze?fileName=${encodeURIComponent(file.name)}`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": mime, ...csrfHeaders() },
      body: file,
    },
  );
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // json dışı gövde
  }
  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; message?: string; details?: unknown };
    throw new ApiError(
      res.status,
      b.error ?? "unknown",
      b.message ?? `HTTP ${res.status.toString()}`,
      b.details,
    );
  }
  return body as CatalogImportAnalysisDto;
}

/** `packageId` verilmezse gövde eskisiyle birebir (anahtar hiç yazılmaz). */
export async function commitCatalogImport(
  fileName: string,
  rows: CatalogImportRowDto[],
  packageId?: string | null,
): Promise<CatalogImportCommitResultDto> {
  return api<CatalogImportCommitResultDto>("/kpi/catalog-import/commit", {
    method: "POST",
    body: packageId ? { fileName, rows, packageId } : { fileName, rows },
  });
}
